import { useEffect } from "react";
import { Form, useActionData, useNavigation, useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { LanguageSwitcher } from "../../components/common/LanguageSwitcher";
import { PasswordInput } from "../../components/common/PasswordInput";
import type { ActionResult } from "../../routes/actions";
import "./auth.css";

export default function RegisterPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const returnUrl = searchParams.get("returnUrl");
  const navigation = useNavigation();
  const actionData = useActionData() as ActionResult | undefined;
  const isSubmitting = navigation.state === "submitting";

  useEffect(() => {
    if (actionData && !actionData.success && actionData.error) {
      toast.error(actionData.error);
    }
  }, [actionData]);

  return (
    <div className="auth-container">
      <div className="auth-language-switcher">
        <LanguageSwitcher variant="standalone" />
      </div>
      <div className="auth-card">
        <div className="auth-header">
          <h1>{t("auth.register.title")}</h1>
          <p>{t("auth.register.subtitle")}</p>
        </div>

        <Form method="post" className="auth-form">
          {actionData && !actionData.success && actionData.error && (
            <div className="auth-error-banner" role="alert">
              {actionData.error}
            </div>
          )}

          <div className={`form-group ${actionData?.fieldErrors?.email ? "error" : ""}`}>
            <label htmlFor="email">{t("auth.register.emailLabel")}</label>
            <input
              id="email"
              name="email"
              type="email"
              required
              placeholder={t("auth.register.emailPlaceholder")}
              autoComplete="email"
            />
            {actionData?.fieldErrors?.email && (
              <span className="form-error">{actionData.fieldErrors.email}</span>
            )}
          </div>

          <div className={`form-group ${actionData?.fieldErrors?.password ? "error" : ""}`}>
            <label htmlFor="password">{t("auth.register.passwordLabel")}</label>
            <PasswordInput
              id="password"
              name="password"
              required
              minLength={8}
              maxLength={128}
              placeholder={t("auth.register.passwordPlaceholder")}
              autoComplete="new-password"
            />
            {actionData?.fieldErrors?.password && (
              <span className="form-error">{actionData.fieldErrors.password}</span>
            )}
          </div>

          <div className={`form-group ${actionData?.fieldErrors?.confirmPassword ? "error" : ""}`}>
            <label htmlFor="confirmPassword">{t("auth.register.confirmPasswordLabel")}</label>
            <PasswordInput
              id="confirmPassword"
              name="confirmPassword"
              required
              minLength={8}
              maxLength={128}
              placeholder={t("auth.register.confirmPasswordPlaceholder")}
              autoComplete="new-password"
            />
            {actionData?.fieldErrors?.confirmPassword && (
              <span className="form-error">{actionData.fieldErrors.confirmPassword}</span>
            )}
          </div>

          <div
            className={`form-group form-group-optional ${actionData?.fieldErrors?.organizationName ? "error" : ""}`}
          >
            <label htmlFor="organizationName">{t("auth.register.organizationLabel")}</label>
            <input
              id="organizationName"
              name="organizationName"
              type="text"
              placeholder={t("auth.register.organizationPlaceholder")}
              autoComplete="organization"
            />
            {actionData?.fieldErrors?.organizationName && (
              <span className="form-error">{actionData.fieldErrors.organizationName}</span>
            )}
          </div>

          <button type="submit" className="btn-primary" disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <span className="loading-spinner"></span> {t("auth.register.submitting")}
              </>
            ) : (
              t("auth.register.submitButton")
            )}
          </button>
        </Form>

        <div className="auth-links">
          <Link
            to={returnUrl ? `/login?returnUrl=${encodeURIComponent(returnUrl)}` : "/login"}
            className="auth-link"
          >
            {t("auth.register.hasAccount")}
          </Link>
        </div>
      </div>
    </div>
  );
}
